/**
 * BOM Context Provider
 * Manages global state for BOM extraction workflow
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  BOMItem,
  ExtractionJob,
  AIModel,
  ToastMessage,
  Component,
  Manufacturer,
  PDFMetadata,
  Dimension,
} from '../types/bom.types';

interface BOMContextType {
  // File state
  uploadedPdfFilename: string | null;
  uploadedPdfUrl: string | null;
  uploadedXlsFilename: string | null;
  uploadedXlsUrl: string | null;
  setUploadedPdf: (filename: string, url: string) => void;
  setUploadedXls: (filename: string, url: string) => void;
  clearUploadedPdf: () => void;
  clearUploadedXls: () => void;

  // Extraction state
  currentPage: number;
  totalPages: number | null;
  extractedItems: BOMItem[];
  selectedModel: AIModel;
  forceReextract: boolean;
  setCurrentPage: (page: number) => void;
  setTotalPages: (total: number | null) => void;
  addExtractedItems: (items: BOMItem[]) => void;
  setExtractedItems: (items: BOMItem[]) => void;
  clearExtractedItems: () => void;
  setSelectedModel: (model: AIModel) => void;
  setForceReextract: (force: boolean) => void;

  // Ask results state (separate tabs)
  components: Component[];
  manufacturers: Manufacturer[];
  metadata: PDFMetadata | null;
  dimensions: Dimension[];
  features: any[];
  setComponents: React.Dispatch<React.SetStateAction<Component[]>>;
  setManufacturers: React.Dispatch<React.SetStateAction<Manufacturer[]>>;
  setMetadata: (metadata: PDFMetadata | null) => void;
  setDimensions: React.Dispatch<React.SetStateAction<Dimension[]>>;
  setFeatures: React.Dispatch<React.SetStateAction<any[]>>;

  // Job state
  currentJobId: string | null;
  setCurrentJobId: (jobId: string | null) => void;

  // Customer folder state (persists across tab switches)
  selectedFolderId: string;
  setSelectedFolderId: (folderId: string) => void;
  useCustomerRules: boolean;
  setUseCustomerRules: (use: boolean) => void;

  // UI state
  loading: boolean;
  loadingMessage: string;
  loadingProgress: { message: string; progress: number; logs: string[] } | null;
  toasts: ToastMessage[];
  setLoading: (loading: boolean, message?: string) => void;
  setLoadingProgress: React.Dispatch<React.SetStateAction<{ message: string; progress: number; logs: string[] } | null>>;
  showToast: (type: ToastMessage['type'], message: string) => void;
  removeToast: (id: string) => void;

  // Excel ground truth
  excelGroundTruth: any[];
  setExcelGroundTruth: (data: any[]) => void;
}

const BOMContext = createContext<BOMContextType | undefined>(undefined);

export const useBOM = (): BOMContextType => {
  const context = useContext(BOMContext);
  if (!context) {
    throw new Error('useBOM must be used within a BOMProvider');
  }
  return context;
};

interface BOMProviderProps {
  children: ReactNode;
}

export const BOMProvider: React.FC<BOMProviderProps> = ({ children }) => {
  // File state
  const [uploadedPdfFilename, setUploadedPdfFilename] = useState<string | null>(null);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [uploadedXlsFilename, setUploadedXlsFilename] = useState<string | null>(null);
  const [uploadedXlsUrl, setUploadedXlsUrl] = useState<string | null>(null);

  // Extraction state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [extractedItems, setExtractedItems] = useState<BOMItem[]>([]);
  const [selectedModel, setSelectedModel] = useState<AIModel>('gpt4o');
  const [forceReextract, setForceReextract] = useState<boolean>(false);

  // Ask results state
  const [components, setComponents] = useState<Component[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [metadata, setMetadata] = useState<PDFMetadata | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [features, setFeatures] = useState<any[]>([]);

  // Job state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Customer folder state (persists across tab switches)
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [useCustomerRules, setUseCustomerRules] = useState<boolean>(true);

  // UI state
  const [loading, setLoadingState] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<{ message: string; progress: number; logs: string[] } | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Excel ground truth
  const [excelGroundTruth, setExcelGroundTruth] = useState<any[]>([]);

  // File management
  const setUploadedPdf = useCallback((filename: string, url: string) => {
    setUploadedPdfFilename(filename);
    setUploadedPdfUrl(url);
  }, []);

  const setUploadedXls = useCallback((filename: string, url: string) => {
    setUploadedXlsFilename(filename);
    setUploadedXlsUrl(url);
  }, []);

  const clearUploadedPdf = useCallback(() => {
    setUploadedPdfFilename(null);
    setUploadedPdfUrl(null);
  }, []);

  const clearUploadedXls = useCallback(() => {
    setUploadedXlsFilename(null);
    setUploadedXlsUrl(null);
  }, []);

  // Extraction management
  const addExtractedItems = useCallback((items: BOMItem[]) => {
    setExtractedItems((prev) => [...prev, ...items]);
  }, []);

  const clearExtractedItems = useCallback(() => {
    setExtractedItems([]);
    setComponents([]);
    setManufacturers([]);
    setMetadata(null);
    setDimensions([]);
    setFeatures([]);
  }, []);

  // Loading management
  const setLoading = useCallback((loading: boolean, message: string = '') => {
    setLoadingState(loading);
    setLoadingMessage(message);
  }, []);

  // Toast management
  const showToast = useCallback((type: ToastMessage['type'], message: string) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: ToastMessage = { id, type, message };
    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value: BOMContextType = {
    // File state
    uploadedPdfFilename,
    uploadedPdfUrl,
    uploadedXlsFilename,
    uploadedXlsUrl,
    setUploadedPdf,
    setUploadedXls,
    clearUploadedPdf,
    clearUploadedXls,

    // Extraction state
    currentPage,
    totalPages,
    extractedItems,
    selectedModel,
    forceReextract,
    setCurrentPage,
    setTotalPages,
    addExtractedItems,
    setExtractedItems,
    clearExtractedItems,
    setSelectedModel,
    setForceReextract,

    // Ask results state
    components,
    manufacturers,
    metadata,
    dimensions,
    features,
    setComponents,
    setManufacturers,
    setMetadata,
    setDimensions,
    setFeatures,

    // Job state
    currentJobId,
    setCurrentJobId,

    // Customer folder state
    selectedFolderId,
    setSelectedFolderId,
    useCustomerRules,
    setUseCustomerRules,

    // UI state
    loading,
    loadingMessage,
    loadingProgress,
    toasts,
    setLoading,
    setLoadingProgress,
    showToast,
    removeToast,

    // Excel ground truth
    excelGroundTruth,
    setExcelGroundTruth,
  };

  return <BOMContext.Provider value={value}>{children}</BOMContext.Provider>;
};

